import {
  Button,
  Modal,
  ModalContent,
  ModalBody,
  ModalFooter,
  Progress,
  ModalHeader,
} from '@heroui/react';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ScrollFooter } from '@/components/layout/ScrollFooter';

export function SurveyLayout({
  answerModal,
  children,
  draftDate,
  disableClose,
  handleSaveAndExit,
  handleNext,
  handleBack,
  index,
  options,
  progress,
  showBack,
  subtitle,
  surveyName,
  title,
  toggleAnswerModal,
}) {
  const { t } = useTranslation('survey');

  const [exitModal, toggleExitModal] = useState(false);
  const [contentModal, toggleContentModal] = useState(false);

  const handleExit = () => {
    toggleExitModal(false);
    handleSaveAndExit();
  };

  return (
    <>
      <Modal isOpen={exitModal} hideCloseButton>
        <ModalContent>
          <ModalHeader>{t('surveylayout.exit_dialog.title')}</ModalHeader>
          <ModalFooter>
            <Button color="primary" onPress={() => toggleExitModal(false)}>
              {t('common.action.cancel')}
            </Button>
            <Button color="primary" onPress={handleExit} variant="bordered">
              {t('common.action.exit_session')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={answerModal} onClose={() => toggleAnswerModal(false)}>
        <ModalContent>
          <ModalHeader>{t('surveylayout.answer_dialog.title')}</ModalHeader>
          <ModalBody>{t('surveylayout.answer_dialog.description')}</ModalBody>
          <ModalFooter>
            <Button color="primary" onPress={() => toggleAnswerModal(false)}>
              {t('common.action.ok')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={contentModal}
        onClose={() => toggleContentModal(false)}
        scrollBehavior="inside"
        size="2xl"
        placement="top"
      >
        <ModalContent>
          <ModalBody>
            <div
              className="ed-v3"
              dangerouslySetInnerHTML={{ __html: options.html }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <div className="flex flex-col min-h-[100dvh]">
        <header className="bg-background sticky top-0 z-10">
          <div className="flex justify-between p-4 items-center">
            <h1 className="font-light overflow-hidden text-xl text-nowrap text-ellipsis ">
              {surveyName}
            </h1>
            <div className="gap-4 inline-flex items-center ">
              {draftDate && (
                <p className="font-light text-sm">
                  {t('surveylayout.draft_date', {
                    date: dayjs(draftDate).fromNow(),
                  })}
                </p>
              )}
              {!disableClose && (
                <Button onPress={() => toggleExitModal(true)} variant="faded">
                  {t('common.action.close')}
                </Button>
              )}
            </div>
          </div>
          <Progress
            aria-label={`${progress}%`}
            className=""
            value={progress}
            color="success"
            radius="none"
          />
        </header>
        <main className="flex flex-1 justify-center overflow-y-auto">
          <div className="flex flex-col w-full max-w-[800px] text-left p-4 md:p-12 space-y-4">
            <h2 className="font-semibold text-lg">{title}</h2>
            <h3>{subtitle}</h3>
            {!_.isEmpty(options.html) && (
              <Button
                color="primary"
                className="place-self-center min-w-1/2 md:min-w-1/4"
                onPress={() => toggleContentModal(true)}
                size="lg"
                variant="flat"
              >
                {options.exampleText || t('common.link.learn_more')}
              </Button>
            )}
            {children}
            {!_.isEmpty(options.image) && (
              <img
                alt={options.alt_text}
                className="place-self-center w-full"
                src={options.image}
              />
            )}
          </div>
        </main>
        <footer className="bg-background bottom-0 sticky">
          <ScrollFooter dependencies={[index]} />
          <div className="flex justify-center p-4 space-x-4">
            {index === 0 && (
              <Button
                className="w-1/2 md:w-1/4"
                color="primary"
                onPress={handleNext}
                size="lg"
              >
                {t('common.action.start')}
              </Button>
            )}
            {index > 0 && (
              <>
                {showBack && (
                  <Button
                    className="bg-background w-full md:w-1/4"
                    color="primary"
                    onPress={handleBack}
                    size="lg"
                    variant="bordered"
                  >
                    {t('common.action.back')}
                  </Button>
                )}
                <Button
                  className="w-full md:w-1/4"
                  color="primary"
                  onPress={handleNext}
                  size="lg"
                >
                  {t('common.action.next')}
                </Button>
              </>
            )}
          </div>
        </footer>
      </div>
    </>
  );
}
